import { signal } from '@preact/signals'
import { useLocation } from 'wouter'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { useAuth } from '../../hooks/useAuth'
import { ROUTES } from '../../routes'
import { styled } from '../../setup'
import { colors, spacing, typography } from '../../theme'

const Container = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${spacing['2xl']};
`

const IconContainer = styled('div')`
  text-align: center;
  margin-bottom: ${spacing['2xl']};
`

const WaitingIcon = styled('div')`
  width: 80px;
  height: 80px;
  margin: 0 auto;
  background: ${colors.successBg};
  border: 2px solid ${colors.successBorder};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
`

const Title = styled('h2')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSize2xl};
  font-weight: ${typography.fontWeightSemibold};
  margin: 0 0 ${spacing.lg} 0;
  text-align: center;
`

const Description = styled('p')`
  color: ${colors.textSecondary};
  font-size: ${typography.fontSizeBase};
  line-height: 1.6;
  margin: 0 0 ${spacing['2xl']} 0;
  text-align: center;
`

const ButtonGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`

const isChecking = signal(false)

export function LinkPendingPage() {
  const { profile, fetchCurrentUser } = useAuth()
  const [, setLocation] = useLocation()

  // Redirect if linked
  if (profile.value?.childLinkState === 'LINKED') {
    setLocation(ROUTES.PROFILE)
    return null
  }

  // Redirect if not pending
  if (profile.value?.childLinkState !== 'PENDING') {
    setLocation(ROUTES.CHILD_LINK_REQUEST)
    return null
  }

  const handleRefresh = async () => {
    isChecking.value = true
    try {
      await fetchCurrentUser()
      if (profile.value?.childLinkState === 'LINKED') {
        setLocation(ROUTES.PROFILE)
      }
    } finally {
      isChecking.value = false
    }
  }

  return (
    <Container>
      <Card maxWidth="480px">
        <IconContainer>
          <WaitingIcon>
            <span role="img" aria-label="Waiting">
              &#9203;
            </span>
          </WaitingIcon>
        </IconContainer>

        <Title>Waiting for Your Guardian</Title>

        <Description>
          We sent an email to your guardian. Once they approve your request, you'll be connected!
          Check back here or ask them to check their email.
        </Description>

        <ButtonGroup>
          <Button variant="primary" onClick={handleRefresh} loading={isChecking.value} fullWidth>
            Check Status
          </Button>
          <Button variant="secondary" onClick={() => setLocation(ROUTES.PROFILE)} fullWidth>
            Back to Profile
          </Button>
        </ButtonGroup>
      </Card>
    </Container>
  )
}
